import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export type ColumnSort = {
  field?: string;
  direction: string;
  icon?: IconDefinition;
};

/**
 * Returns one of two labels based on a boolean value.
 *
 * @param value - The value that selects the label.
 * @param opt1 - The label returned for `true`.
 * @param opt2 - The label returned for `false`.
 */
export const renderBoolean = (value: boolean, opt1 = 'Yes', opt2 = 'No') =>
  value ? opt1 : opt2;

/**
 * Formats a date string using the runtime's locale.
 *
 * @param value - The date string to format. The sentinel value `1/1/0001` is
 * treated as missing.
 * @param isNullOpt - The text returned when the value is missing.
 */
export const renderDate = (
  value: string | null | undefined,
  isNullOpt = '-',
) =>
  value != '1/1/0001' && value
    ? new Date(value).toLocaleDateString()
    : isNullOpt;

/**
 * Returns a string value or the configured fallback when the value is empty.
 *
 * @param val - The value to render.
 * @param isNullOpt - The fallback for an empty, `null`, or `undefined` value.
 */
export const renderNullValueAsDash = (
  val: string | null | undefined,
  isNullOpt?: string,
) => val || isNullOpt;

/**
 * Sorts rows by a field containing string, number, boolean, or `Date` values.
 * Fields whose names contain "date" are parsed as date values and place empty
 * dates at the start for descending sorts or at the end for ascending sorts.
 *
 * @remarks Non-date sorts mutate `arr`. Date-field sorts return a new array.
 * @typeParam T - The row type.
 * @param arr - The rows to sort.
 * @param field - The field name to sort by.
 * @param direction - The sort selector. `desc` applies an order multiplier of `-1`; all other values apply `1`.
 * @returns The sorted rows.
 */
export const universalSort = <T extends object>(
  arr: T[],
  field: string,
  direction: string,
): T[] => {
  // set the order of the sorted array
  // -1 = descending
  // 1 = ascending
  const order = direction === 'desc' ? -1 : 1;
  const sortField = field as keyof T;

  if (field.toLowerCase().includes('date')) {
    return sortByDate(arr, sortField, order);
  }
  return arr.sort((a, b) => {
    const valueA = a[sortField];
    const valueB = b[sortField];

    // check if the field is a string
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return order * valueB.localeCompare(valueA);
    }
    // check if the field is a number
    else if (typeof valueA === 'number' && typeof valueB === 'number') {
      return order * (valueA - valueB);
    }
    // check if the field is a boolean
    else if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
      return order * (valueA === valueB ? 0 : (valueA && -1) || 1);
    }
    // check if the field is a date
    else if (valueA instanceof Date && valueB instanceof Date) {
      return order * (valueA.getTime() - valueB.getTime());
    }
    return 0;
  });
};

/**
 * Sorts rows by a field containing date values while preserving empty dates.
 *
 * @typeParam T - The row type.
 * @typeParam K - A field on the row type.
 * @param arr - The rows to sort.
 * @param field - The date field to sort by.
 * @param order - `-1` for descending order or `1` for ascending order.
 * @returns A new array containing the sorted rows.
 */
function sortByDate<T, K extends keyof T>(
  arr: T[],
  field: K,
  order: -1 | 1,
): T[] {
  const emptyDates: T[] = [];
  let hasDates: T[] = [];
  arr.forEach((a) => {
    if (
      a[field] === '' ||
      a[field] === null ||
      a[field] === undefined ||
      a[field] === '1/1/0001'
    ) {
      emptyDates.push(a);
    } else {
      hasDates.push(a);
    }
  });

  hasDates = hasDates.sort((a, b) => {
    const dateA = new Date(a[field] as string | number);
    const dateB = new Date(b[field] as string | number);

    if (dateA.toString() === 'Invalid Date') {
      return order === -1 ? 1 : -1;
    }

    return dateB.getTime() - dateA.getTime();
  });
  const returnArray = [...emptyDates, ...hasDates];

  return order === -1 ? returnArray : returnArray.reverse();
}

/**
 * Advances a column through the descending, ascending, and unsorted states.
 * Selecting a different sortable column starts it in descending order. A
 * column with `sortable: false` leaves the current sort unchanged.
 *
 * @param column - The selected column and its optional sortable setting.
 * @param currentSort - The sort currently applied to the grid.
 * @param defaultIcon - The icon for the unsorted state.
 * @param ascIcon - The icon for the ascending state.
 * @param descIcon - The icon for the descending state.
 * @returns The next column sort state.
 */
export const handleSettingColumnSort = (
  { fieldName, sortable }: { fieldName: string; sortable?: boolean },
  currentSort: ColumnSort,
  defaultIcon?: IconDefinition,
  ascIcon?: IconDefinition,
  descIcon?: IconDefinition,
): ColumnSort => {
  let newSort: ColumnSort;
  if (typeof sortable !== 'boolean' || sortable) {
    if (fieldName === currentSort.field) {
      if (currentSort.direction === 'desc') {
        newSort = {
          ...currentSort,
          direction: 'asc',
          icon: ascIcon,
        };
      } else {
        newSort = { field: '', direction: '', icon: defaultIcon };
      }
    } else {
      newSort = {
        field: fieldName,
        direction: 'desc',
        icon: descIcon,
      };
    }
    return newSort;
  }
  return currentSort;
};
